import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import noop from 'lodash/noop'

import { breakpoints, colors, fontSizes } from '@/styles'
import ErrorPage from '@/features/ErrorPage'
import { MessageModalProvider } from '@/features/ListingsMap/map-list/context'
import Pagination from '@/components/pagination'
import PaginationExt from '@/components/pagination-ext'

import FeaturedListings from './featured-listings'
import List from './list'

const Wrapper = styled.div`
  max-width: calc(${breakpoints.desktop} + 40px);
  padding: 24px 20px 0;
  flex: 1;
  overflow: auto;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const EmptyContainer = styled.div`
  min-height: 120vh;
`

const PaginationSummary = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-bottom: 60px;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: 600;
  letter-spacing: -0.2px;
  line-height: 22px;
`

const PaginationSummaryExt = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-bottom: 60px;
  color: ${colors.grey48};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.2px;
  line-height: 22px;
`

const FullList = ({
  featuredListings,
  listings,
  metadata,
  setFilter,
  isLoading,
  preload = noop,
  companyName,
  isRented,
  isExt,
  maxCol = 3
}) => {
  const { t } = useTranslation()

  if (isLoading) return <EmptyContainer />

  if (!listings || !listings.length) {
    const errorText = companyName ? (
      isRented ? (
        t(
          'b.error_page.view.company_page_rented.text',
          'No rented listings available to display at this time.'
        )
      ) : (
        <Trans
          i18nKey="b.error_page.view.company_page_name.text"
          values={{ companyName }}
          defaults="There are currently no available rental listings from {{ companyName }}."
        />
      )
    ) : (
      t(
        'b.error_page.view.company_page.text',
        'No listings available at this time. Please check back soon.'
      )
    )
    return (
      <ErrorPage
        hideSearch
        type={ErrorPage.Types.CompanyPage}
        errorText={errorText}
      />
    )
  }

  if (!metadata) return null

  const handlePageChange = ({ currentPage }) => {
    if (currentPage === metadata.page) return
    setFilter({ page: currentPage })
    window.scrollTo(0, 0)
  }

  const totalPages = Math.ceil(metadata.total_count / metadata.page_size)
  const firstItem = (metadata.page - 1) * metadata.page_size + 1
  const lastItem = Math.min(
    metadata.page * metadata.page_size,
    metadata.total_count
  )

  return (
    <Wrapper>
      <MessageModalProvider>
        <List listings={listings} maxCol={maxCol} />
        {isExt ? (
          <>
            <PaginationExt
              currentPage={metadata.page}
              totalRecords={metadata.total_count}
              pageLimit={metadata.page_size}
              totalPages={totalPages}
              pageNeighbours={2}
              onPageChanged={handlePageChange}
              preload={page => {
                preload({ page })
              }}
            />
            {metadata.total_count > metadata.page_size && (
              <PaginationSummaryExt>
                Showing {firstItem} - {`${lastItem} `}
                <Trans
                  i18nKey="b.listings.view.position_in_listings.text"
                  values={{ totalListings: metadata.total_count }}
                  defaults="of {{totalListings}} listings"
                />
              </PaginationSummaryExt>
            )}
            {!!featuredListings?.length && (
              <FeaturedListings listings={featuredListings} />
            )}
          </>
        ) : (
          <>
            <Pagination
              currentPage={metadata.page}
              totalRecords={metadata.total_count}
              pageLimit={metadata.page_size}
              totalPages={totalPages}
              pageNeighbours={2}
              onPageChanged={handlePageChange}
              preload={page => {
                preload({ page })
              }}
            />
            {metadata.total_count > metadata.page_size && (
              <PaginationSummary>
                {firstItem} - {`${lastItem} `}
                <Trans
                  i18nKey="b.listings.view.position_in_listings.text"
                  values={{ totalListings: metadata.total_count }}
                  defaults="of {{totalListings}} listings"
                />
              </PaginationSummary>
            )}
            {!!featuredListings?.length && (
              <FeaturedListings listings={featuredListings} />
            )}
          </>
        )}
      </MessageModalProvider>
    </Wrapper>
  )
}

export default FullList
